import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { Router, RouterOutlet, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { HttpClient, HttpContext } from '@angular/common/http';
import { filter, map, startWith } from 'rxjs';
import { AuthService } from './core/auth/auth.service';
import { SessionService } from './core/session/session.service';
import { SandboxService } from './shared/services/sandbox.service';
import { I18nService } from './shared/services/i18n.service';
import { NavigationHistoryService } from './core/navigation-history.service';
import { SKIP_LOADING } from './core/interceptors/loading.interceptor';
import { LoadingService } from './core/services/loading.service';
import { LoadingStyleService } from './core/services/loading-style.service';
import { RoadLoadingBarComponent } from './shared/components/road-loading-bar.component';
import { environment } from '../environments/environment';
import { SubscriptionPlan } from './shared/models/subscription.model';

const PLAN_BADGE: Record<SubscriptionPlan, { label: string; cls: string }> = {
  free:       { label: 'Starter',  cls: 'bg-slate-100 text-slate-500' },
  basic:      { label: 'Commuter', cls: 'bg-blue-50 text-blue-700' },
  expert:     { label: 'Road Pro', cls: 'bg-purple-50 text-purple-700' },
  enterprise: { label: 'Fleet',    cls: 'bg-amber-50 text-amber-700' },
};

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, RoadLoadingBarComponent, FormsModule, Select],
  styles: [`
    @keyframes drive-dash {
      0%, 100% { transform: translateX(0); }
      50%      { transform: translateX(3px); }
    }
    @keyframes trail-fade {
      0%   { opacity: .6; width: 12px; }
      100% { opacity: 0;  width: 1px; }
    }
    .logo-driving { overflow: visible; }
    .logo-driving svg { animation: drive-dash .9s ease-in-out infinite; }
    .logo-driving::before {
      content: '';
      position: absolute;
      left: 2px;
      top: 50%;
      height: 2px;
      border-radius: 1px;
      background: currentColor;
      transform: translateY(-50%);
      animation: trail-fade .9s ease-out infinite;
    }
  `],
  template: `
    <app-road-loading-bar />
    @if (session.expired()) {
      <div class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
        <div class="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 text-center">
          <i class="pi pi-clock text-3xl text-amber-500 mb-3"></i>
          <h2 class="text-base font-semibold text-slate-800 mb-1.5">{{ t('session.expiredTitle') }}</h2>
          <p class="text-sm text-slate-500 mb-5">{{ t('session.expiredMessage') }}</p>
          <button (click)="reauthenticate()"
                  class="w-full py-2 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors">
            {{ t('session.logInAgain') }}
          </button>
        </div>
      </div>
    }
    @if (hideShell()) {
      <router-outlet />
    } @else {
    <div class="flex h-screen bg-[#1B1914] overflow-hidden">

      <!-- Mobile backdrop -->
      @if (sidebarOpen()) {
        <div class="fixed inset-0 bg-black/30 z-30 md:hidden"
             (click)="sidebarOpen.set(false)"></div>
      }

      <!-- Sidebar -->
      <aside class="fixed inset-y-0 left-0 z-40 w-60 bg-white border-r border-slate-200 flex flex-col
                    transition-transform duration-200 ease-in-out
                    md:relative md:translate-x-0"
             [class.-translate-x-full]="!sidebarOpen()">

        <!-- Logo row -->
        <div class="px-5 h-14 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <div class="flex items-center gap-2">
            <div class="relative w-7 h-7 flex-shrink-0 flex items-center justify-center bg-brand-500/15 border border-brand-500/25 rounded-lg text-brand-500"
                 [class.logo-driving]="showLogoDriving()">
              <svg width="13" height="13" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,14 12,9 6,4"/></svg>
            </div>
            <span class="font-bold text-[15px] tracking-tight text-[#EDE9E0]">Drive<span class="text-brand-500">Log</span></span>
          </div>
          <button (click)="sidebarOpen.set(false)"
            class="md:hidden text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors">
            <i class="pi pi-times text-sm"></i>
          </button>
        </div>

        <!-- Nav -->
        <nav class="flex-1 p-3 space-y-1">
          <a routerLink="/dashboard" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-home text-base"></i> {{ t('nav.dashboard') }}
          </a>
          <a routerLink="/vehicles" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-car text-base"></i> {{ t('nav.vehicles') }}
          </a>
          <a routerLink="/stations" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-map-marker text-base"></i> {{ t('nav.stations') }}
          </a>
          <a routerLink="/service-points" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-shop text-base"></i> {{ t('nav.servicePoints') }}
          </a>
          <a routerLink="/service-types" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-wrench text-base"></i> {{ t('nav.serviceTypes') }}
          </a>
          <a routerLink="/expense-types" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-wallet text-base"></i> {{ t('nav.expenseTypes') }}
          </a>
          <a routerLink="/reports" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
             (click)="sidebarOpen.set(false)"
             class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
            <i class="pi pi-chart-bar text-base"></i> {{ t('nav.reports') }}
          </a>

          <div class="pt-2 mt-2 border-t border-slate-100">
            <a routerLink="/subscription" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
               (click)="sidebarOpen.set(false)"
               class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
              <i class="pi pi-credit-card text-base"></i>
              <span class="flex-1">{{ t('nav.myPlan') }}</span>
              @if (planBadge()) {
                <span class="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      [class]="planBadge()!.cls">
                  {{ planBadge()!.label }}
                </span>
              }
            </a>
            <a routerLink="/changelog" routerLinkActive="bg-brand-50 text-brand-600 font-medium"
               (click)="sidebarOpen.set(false)"
               class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors text-sm">
              <i class="pi pi-sparkles text-base"></i> {{ t('nav.changelog') }}
            </a>
          </div>

          @if (isAdmin) {
            <div class="pt-2 mt-2 border-t border-slate-100">
              <a routerLink="/admin" routerLinkActive="bg-slate-100 text-slate-700 font-medium"
                 (click)="sidebarOpen.set(false)"
                 class="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors text-sm">
                <i class="pi pi-cog text-base"></i> {{ t('nav.adminConsole') }}
              </a>
            </div>
          }
        </nav>

        <!-- Version -->
        <div class="px-5 py-3 border-t border-slate-100">
          <span class="text-xs text-slate-300">v{{ version }}</span>
        </div>
      </aside>

      <!-- Content column -->
      <div class="flex-1 flex flex-col min-w-0">

        <!-- Universal header -->
        <header class="flex items-center justify-between px-4 h-14 bg-white border-b border-slate-200 flex-shrink-0">
          <div class="flex items-center gap-3">
            <button (click)="sidebarOpen.set(true)"
              class="md:hidden text-slate-500 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
              <i class="pi pi-bars text-base"></i>
            </button>
            <div class="md:hidden flex items-center gap-2">
              <div class="relative w-6 h-6 flex-shrink-0 flex items-center justify-center bg-brand-500/15 border border-brand-500/25 rounded-md text-brand-500"
                   [class.logo-driving]="showLogoDriving()">
                <svg width="11" height="11" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,14 12,9 6,4"/></svg>
              </div>
              <span class="font-bold text-sm tracking-tight text-[#EDE9E0]">Drive<span class="text-brand-500">Log</span></span>
            </div>
            <span class="hidden md:block text-sm font-medium text-slate-700">{{ pageTitle() }}</span>
          </div>

          <!-- Bell + user menu -->
          <div class="flex items-center gap-1">

            <a routerLink="/notifications"
               class="relative p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">
              <i class="pi pi-bell text-base"></i>
              @if (unreadNotifCount() > 0) {
                <span class="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500"></span>
              }
            </a>

            <div class="relative">
              <button (click)="userMenuOpen.set(!userMenuOpen())"
                      class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-slate-600
                             hover:bg-slate-100 transition-colors">
                <i class="pi pi-user text-xs"></i>
                <span class="hidden sm:inline truncate max-w-36">{{ user()?.name }}</span>
                <i class="pi pi-chevron-down text-[10px] text-slate-400 transition-transform duration-150"
                   [class.rotate-180]="userMenuOpen()"></i>
              </button>

              @if (userMenuOpen()) {
                <div class="fixed inset-0 z-40" (click)="userMenuOpen.set(false)"></div>
                <div class="absolute right-0 top-full mt-1.5 w-52 bg-white border border-slate-200
                            rounded-xl shadow-lg z-50 py-1.5 overflow-hidden">
                  <a routerLink="/profile" (click)="userMenuOpen.set(false)"
                     class="flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-600
                            hover:bg-slate-50 transition-colors">
                    <i class="pi pi-user text-xs text-slate-400"></i>
                    {{ t('menu.profile') }}
                  </a>
                  <a routerLink="/notifications" (click)="userMenuOpen.set(false)"
                     class="flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-600
                            hover:bg-slate-50 transition-colors">
                    <i class="pi pi-bell text-xs text-slate-400"></i>
                    <span class="flex-1">{{ t('menu.notifications') }}</span>
                    @if (unreadNotifCount() > 0) {
                      <span class="text-[10px] font-bold bg-red-100 text-red-600 rounded-full px-1.5 py-0.5">
                        {{ unreadNotifCount() }}
                      </span>
                    }
                  </a>
                  <a routerLink="/feedback" (click)="userMenuOpen.set(false)"
                     class="flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-600
                            hover:bg-slate-50 transition-colors">
                    <i class="pi pi-comments text-xs text-slate-400"></i>
                    {{ t('menu.feedback') }}
                  </a>
                  <a routerLink="/settings" (click)="userMenuOpen.set(false)"
                     class="flex items-center gap-2.5 px-3.5 py-2 text-sm text-slate-600
                            hover:bg-slate-50 transition-colors">
                    <i class="pi pi-sliders-h text-xs text-slate-400"></i>
                    {{ t('menu.settings') }}
                  </a>
                  <div class="border-t border-slate-100 my-1"></div>
                  <button (click)="logout()"
                          class="w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-600
                                 hover:bg-red-50 transition-colors">
                    <i class="pi pi-sign-out text-xs"></i>
                    {{ t('menu.signOut') }}
                  </button>
                </div>
              }
            </div>

          </div>
        </header>

        <!-- Sandbox banner -->
        @if (isAdmin) {
          <div class="flex items-center gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 flex-shrink-0">
            <div class="flex items-center gap-1.5 text-amber-600 text-xs font-semibold">
              <i class="pi pi-box text-amber-500 text-sm"></i>
              {{ t('sandbox.label') }}
            </div>
            <div class="w-px h-4 bg-amber-200"></div>
            <span class="text-xs text-amber-600">{{ t('sandbox.viewingAs') }}</span>
            <p-select [options]="sandbox.users" optionLabel="name" optionValue="id"
                      [ngModel]="sandbox.viewingAs()" (ngModelChange)="onSandboxChange($event)" />
            <div class="flex-1"></div>
            <a routerLink="/admin"
               class="flex items-center gap-1.5 text-xs text-amber-600 hover:text-amber-800
                      px-2 py-1 rounded-lg hover:bg-amber-100 transition-colors">
              <i class="pi pi-cog text-xs"></i>
              {{ t('sandbox.adminConsole') }}
            </a>
          </div>
        }

        <main class="flex-1 overflow-auto">
          <router-outlet />
        </main>
      </div>
    </div>
    }
  `,
})
export class AppComponent implements OnInit {
  private auth   = inject(AuthService);
  private router = inject(Router);
  private http   = inject(HttpClient);
  readonly sandbox = inject(SandboxService);
  readonly i18n    = inject(I18nService);
  readonly session = inject(SessionService);
  readonly loading = inject(LoadingService);
  readonly loadingStyle = inject(LoadingStyleService);
  readonly showLogoDriving = computed(() => this.loading.isLoading() && this.loadingStyle.style() === 'logo');
  // Eagerly instantiate so it starts tracking NavigationEnd events from app bootstrap,
  // before the Feedback page (a lazy route) would otherwise create it too late to see
  // the navigation that brought the user there.
  private navHistory = inject(NavigationHistoryService);

  readonly t = (key: Parameters<I18nService['t']>[0]) => this.i18n.t(key);

  readonly version = environment.version;
  sidebarOpen      = signal(false);
  userMenuOpen     = signal(false);
  planBadge        = signal<{ label: string; cls: string } | null>(null);
  unreadNotifCount = signal(0);

  ngOnInit() {
    this.loadUnreadCount();
    this.loadingStyle.load();
  }

  hideShell = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => {
        const hidden = this.shouldHideShell();
        if (!hidden) { this.loadPlanBadge(); this.loadUnreadCount(); }
        return hidden;
      }),
      startWith(this.shouldHideShell()),
    ),
    { initialValue: true },
  );

  private routeUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  // Reactive to both route changes AND locale changes (t() reads locale signal)
  pageTitle = computed(() => {
    const url = this.routeUrl() ?? '';
    if (url.includes('/events'))          return this.i18n.t('title.events');
    if (url.startsWith('/vehicles'))      return this.i18n.t('title.vehicles');
    if (url.startsWith('/stations'))      return this.i18n.t('title.stations');
    if (url.startsWith('/service-points')) return this.i18n.t('title.servicePoints');
    if (url.startsWith('/service-types')) return this.i18n.t('title.serviceTypes');
    if (url.startsWith('/expense-types')) return this.i18n.t('title.expenseTypes');
    if (url.startsWith('/subscription'))  return this.i18n.t('title.subscription');
    if (url.startsWith('/reports'))       return this.i18n.t('title.reports');
    if (url.startsWith('/profile'))       return this.i18n.t('title.profile');
    if (url.startsWith('/settings'))      return this.i18n.t('title.settings');
    if (url.startsWith('/notifications')) return this.i18n.t('title.notifications');
    if (url.startsWith('/changelog'))     return this.i18n.t('title.changelog');
    if (url.startsWith('/feedback'))      return this.i18n.t('title.feedback');
    return this.i18n.t('title.dashboard');
  });

  user    = () => this.auth.getUser();
  get isAdmin(): boolean { return this.auth.getUser()?.roles.includes('admin') ?? false; }
  logout() { this.auth.logout(); }

  reauthenticate() {
    this.session.clear();
    this.auth.login();
  }

  private silent = new HttpContext().set(SKIP_LOADING, true);

  loadUnreadCount() {
    this.http.get<{ read: boolean }[]>('/api/notifications', { context: this.silent }).subscribe({
      next: list => this.unreadNotifCount.set(list.filter(n => !n.read).length),
      error: () => {},
    });
  }

  loadPlanBadge() {
    this.http.get<{ subscription: { plan: SubscriptionPlan } }>('/api/subscription', { context: this.silent }).subscribe({
      next: d => this.planBadge.set(PLAN_BADGE[d.subscription.plan] ?? null),
      error: () => {},
    });
  }

  onSandboxChange(id: string): void {
    this.sandbox.viewingAs.set(id);
    this.loadPlanBadge();
  }

  private shouldHideShell(): boolean {
    const url = this.router.url;
    return !this.auth.isAuthenticated()
      || url.startsWith('/login')
      || url.startsWith('/admin');
  }
}
